/**
 * Orders service — EG-78 Phase 1 aggregate.
 *
 * Naming: snake_case match BE JSON per ADR-013.
 * Wrapper: BE return `{ success, data }` → unwrap qua `services/http.ts`.
 * Group filter: FE gửi `channel_group=livestream` → BE EG-376 resolve `IN [FACEBOOK, TIKTOK]`.
 */

import api from './api';
import { unwrap } from './http';
import type { TenantOrder, OrderSummary } from '@/mocks/orders';
import type { OrderListQuery } from './queryKeys';
import { CHANNEL_GROUP_MAP, STATUS_GROUP_MAP } from '@/pages/Orders/parts/constants';
import type { OrderChannelGroup, OrderStatusGroup } from '@/pages/Orders/parts/constants';

/** EG-78 aggregate response shape (snake_case per ADR-013 D4). */
export interface OrderListResponse {
  items: TenantOrder[];
  total: number;
  summary: OrderSummary;
}

/** Raw order from BE API — before FE mapping. */
interface RawOrder {
  _id: string;
  order_number: string;
  order_date: string;
  channel: string;
  state: string;
  payment: { method: string };
  shipping: { address?: { name: string; phone?: string } };
  buyer_invoice_info?: { name: string; phone?: string };
  summary: {
    total: number;
    final_price?: number;
    subtotal?: number;
    total_discount?: number;
    direct_discount?: number;
  };
  created_at: string;
  commission_staff?: Array<{ staff_id: string; role_code: string }>;
  process_by?: string;
  cashier_name?: string;
}

function mapRawOrder(raw: RawOrder): TenantOrder {
  const rawItems = (raw as unknown as Record<string, unknown>).items as Array<Record<string, unknown>> | undefined;
  return {
    _id: raw._id,
    order_number: raw.order_number,
    channel: raw.channel as TenantOrder['channel'],
    state: raw.state as TenantOrder['state'],
    total_amount: raw.summary?.total ?? 0,
    customer: {
      name: raw.buyer_invoice_info?.name ?? raw.shipping?.address?.name ?? '',
      phone: raw.buyer_invoice_info?.phone ?? raw.shipping?.address?.phone,
    },
    cashier: raw.cashier_name
      ? { name: raw.cashier_name, staff_id: raw.commission_staff?.find(s => s.role_code === 'NVBH')?.staff_id }
      : raw.commission_staff?.find(s => s.role_code === 'NVBH')
        ? { name: '', staff_id: raw.commission_staff.find(s => s.role_code === 'NVBH')?.staff_id }
        : undefined,
    payment_method: raw.payment?.method,
    created_at: raw.created_at,
    items: rawItems?.map(i => ({
      sku: String(i.sku ?? i.product_variant_id ?? ''),
      product_name: String(i.name ?? i.product_id ?? ''),
      qty: Number(i.quantity ?? 0),
      price: Number(i.price ?? i.original_price ?? 0),
    })) ?? [],
  };
}

function mapRawOrderDetail(raw: RawOrder & Record<string, unknown>): TenantOrder {
  const rawItems = raw.items as Array<Record<string, any>> | undefined;
  return {
    _id: raw._id || (raw as any).order_id,
    order_number: raw.order_number,
    channel: raw.channel as TenantOrder['channel'],
    state: (raw.state || raw.status) as TenantOrder['state'],
    total_amount: raw.summary?.total ?? Number(raw.total_amount) ?? 0,
    subtotal: raw.summary?.final_price ?? raw.summary?.subtotal ?? (raw as any).subtotal,
    discount: raw.summary?.total_discount ?? raw.summary?.direct_discount ?? (raw as any).discount,
    customer: (raw as any).customer ? (raw as any).customer : {
      name: (raw.buyer_invoice_info as { name?: string } | undefined)?.name
        ?? (raw.shipping as { address?: { name?: string } } | undefined)?.address?.name
        ?? '',
      phone: (raw.buyer_invoice_info as { phone?: string } | undefined)?.phone
        ?? (raw.shipping as { address?: { phone?: string } } | undefined)?.address?.phone,
    },
    payment_method: (raw.payment as { method?: string } | undefined)?.method || (raw as any).payment_method,
    cashier: (raw as any).cashier ? (raw as any).cashier : ((raw as any).cashier_name
      ? { name: (raw as any).cashier_name, staff_id: raw.commission_staff?.find(s => s.role_code === 'NVBH')?.staff_id }
      : raw.commission_staff?.find(s => s.role_code === 'NVBH')
        ? { name: '', staff_id: raw.commission_staff.find(s => s.role_code === 'NVBH')?.staff_id }
        : undefined),
    care_staff: (raw as any).care_staff,
    created_at: raw.created_at,
    note: (raw.notes as { user_note?: string } | undefined)?.user_note || (raw as any).note,
    items: rawItems?.map(i => ({
      sku: String(i.sku || i.product_variant_id || ''),
      product_name: String(i.product_name || i.name || ''),
      qty: Number(i.qty || i.quantity || 0),
      price: Number(i.price || i.unit_price || 0),
      price_method: i.price_method || 'fixed',
      warehouse_name: i.warehouse_name || '',
      serial: i.serial || '',
      warehouse_staff: i.warehouse_staff,
    })) ?? [],
    shipping: (raw as any).shipping,
    buyer_invoice_info: raw.buyer_invoice_info || (raw as any).buyer_invoice_info,
    store: (raw as any).store,
    invoice_info: raw.invoice_info || (raw as any).invoice_info,
    misa_sale_voucher_code: raw.misa_sale_voucher_code || (raw as any).misa_sale_voucher_code,
    misa_export_voucher_code: raw.misa_export_voucher_code || (raw as any).misa_export_voucher_code,
    state_history: raw.state_history || (raw as any).state_history,
  };
}

/**
 * Legacy POS create order payload — TODO: refactor sang shared schema khi POS module update.
 * Channel union 'pos|online|wholesale' KHÔNG khớp shared OrderChannel 11 enum.
 */
export interface CreateOrderPayload {
  customer_id?: string;
  items: Array<{ product_id: string; qty: number; price: number; discount?: number }>;
  payment_method: 'cash' | 'card' | 'transfer' | 'split';
  channel: 'pos' | 'online' | 'wholesale';
  discount?: number;
  notes?: string;
}

export interface UpdateOrderPayload {
  notes?: string;
  store_id?: string;
  sales_person_id?: string;
  care_staff_id?: string;
  customer_id?: string;
  customer_info?: any;
  shipping?: any;
  buyer_invoice_info?: any;
  items?: Array<{
    sku: string;
    qty?: number;
    price?: number;
    price_method?: string;
    warehouse_name?: string;
    serial?: string;
    warehouse_staff_id?: string;
  }>;
}

export const orderService = {
  list: (q: OrderListQuery) => {
    // Resolve group → array enum string cho BE EG-376
    const params: Record<string, unknown> = {
      page: q.page,
      page_size: q.page_size,
      search: q.search,
      date_from: q.date_from,
      date_to: q.date_to,
    };
    if (q.channel_group) {
      params.channel = CHANNEL_GROUP_MAP[q.channel_group as OrderChannelGroup]?.join(',');
    }
    if (q.status_group) {
      params.state = STATUS_GROUP_MAP[q.status_group as OrderStatusGroup]?.join(',');
    }
    return api.get('/orders', { params }).then((r) => {
      const raw = unwrap<{ items: RawOrder[]; total: number; summary: OrderSummary }>(r.data);
      return {
        items: raw.items?.map((r) => mapRawOrder(r as RawOrder & Record<string, unknown>)) ?? [],
        total: raw.total ?? 0,
        summary: raw.summary ?? { total_amount: 0, count_by_channel: {} as Record<string, number> },
      };
    });
  },

  getById: (id: string) =>
    api.get(`/orders/${id}`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  revealSensitive: (id: string, password: string) =>
    api.post(`/orders/${id}/sensitive`, { password }).then((r) => unwrap<{ citizen_id?: string; invoice_buyer_cccd?: string }>(r.data)),

  listConfirmed: (q: { page?: number; page_size?: number; search?: string }) =>
    api.get('/orders', {
      params: { ...q, state: 'CONFIRMED,CUSTOMER_CARE_CONFIRMED,WAITING_PICKUP' },
    }).then((r) => unwrap<OrderListResponse>(r.data)),

  cancel: (id: string, reason: string) =>
    api.post(`/orders/${id}/cancel`, { reason }).then((r) => unwrap<TenantOrder>(r.data)),

  confirm: (id: string) =>
    api.post(`/orders/${id}/confirm`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  ship: (id: string, tracking_number: string, carrier: string) =>
    api.post(`/orders/${id}/ship`, { tracking_number, carrier }).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  complete: (id: string) =>
    api.post(`/orders/${id}/complete`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  // Legacy POS endpoints — keep cho POS module hiện tại, refactor khi POS update sang shared schema
  create: (body: CreateOrderPayload) =>
    api.post<TenantOrder>('/orders', body).then((r) => r.data),

  createPosOrder: (body: CreateOrderPayload) =>
    api.post<TenantOrder>('/orders/pos', body).then((r) => r.data),

  history: () => api.get<TenantOrder[]>('/orders/history').then((r) => r.data),

  exportList: (filter: OrderListQuery) => {
    const params: Record<string, unknown> = {
      search: filter.search,
      date_from: filter.date_from,
      date_to: filter.date_to,
    };
    if (filter.channel_group) {
      params.channel = CHANNEL_GROUP_MAP[filter.channel_group as OrderChannelGroup]?.join(',');
    }
    if (filter.status_group) {
      params.state = STATUS_GROUP_MAP[filter.status_group as OrderStatusGroup]?.join(',');
    }
    return api.get('/orders/export', { params }).then((r) => {
      const raw = unwrap<{ items: RawOrder[]; total: number }>(r.data);
      return {
        items: raw.items?.map((item) => mapRawOrder(item as RawOrder & Record<string, unknown>)) ?? [],
        total: raw.total ?? 0,
      };
    });
  },

  update: (id: string, payload: UpdateOrderPayload) =>
    api.patch(`/orders/${id}`, payload).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  updateNote: (id: string, note: string) =>
    orderService.update(id, { notes: note }),

  updateBranch: (id: string, branch_id: string) =>
    orderService.update(id, { store_id: branch_id }),

  updateStaff: (id: string, staff_id: string) =>
    orderService.update(id, { sales_person_id: staff_id }),

  updateWarehouseStaff: (id: string, item_sku: string, staff_id: string, _staff_name: string) =>
    orderService.update(id, { items: [{ sku: item_sku, warehouse_staff_id: staff_id }] }),

  updateCustomer: (id: string, customer_id: string) =>
    orderService.update(id, { customer_id }),

  updateShipping: (id: string, shipping: { name: string; phone: string; address: string; province?: string; citizen_id?: string; delivery_days?: number; expected_delivery_date?: string; delivery_method?: string }) =>
    orderService.update(id, { shipping }),

  updateInvoice: (id: string, invoice: { name?: string; tax_id?: string; address?: string; email?: string; buyer_name?: string; buyer_cccd?: string }) =>
    orderService.update(id, { buyer_invoice_info: invoice }),

  updateItems: (id: string, items: Array<{ sku: string; qty: number; price: number; price_method?: string; warehouse_name?: string; serial?: string }>) =>
    orderService.update(id, { items }),

  createReceipt: (id: string, receipt: { type: 'cash' | 'transfer' | 'split'; doc_date: string; posting_date: string; reason?: string; details: Array<{ description?: string; bank_name?: string; bank_account_debt?: string; bank_account_credit?: string; amount: number }> }) =>
    api.post(`/orders/${id}/receipts`, receipt).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  syncMisa: (id: string) =>
    api.post(`/orders/${id}/sync-misa`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  getAuditHistory: (id: string, page: number = 1, pageSize: number = 20) =>
    api.get(`/orders/${id}/audit-history`, { params: { page, pageSize } }).then((r) => unwrap<any>(r.data)),
};

